import { Box, Flex, Text, type TextProps } from '@chakra-ui/react';

import {
  PLAN_CHART_CARD_RADIUS,
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
  controlHeight,
} from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import { fontSize } from 'styles/revampTokens';

import colors from './dataTheme';

import type { ReactNode } from 'react';

/** Small uppercase label that titles a section (VERSIONS, ACTIONS, ...). */
export const SectionTitle = ({ children, ...rest }: { children: ReactNode } & TextProps) => (
  <Text
    {...PLAN_TEXT.labelSmMedium}
    color={colors.textSecondary}
    textTransform='uppercase'
    letterSpacing='0.06em'
    {...rest}
  >
    {children}
  </Text>
);

/** White surface with the Plan card radius and a single hairline. */
export const cardStyle = {
  bg: colors.surface,
  border: '1px solid',
  borderColor: colors.border,
  borderRadius: PLAN_CHART_CARD_RADIUS,
} as const;

/** Neutral bordered control (filter / sort / dropdown triggers). */
export const controlStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  h: controlHeight,
  px: '10px',
  border: '1px solid',
  borderColor: colors.border,
  borderRadius: PLAN_REVAMP_RADIUS_SM,
  bg: colors.surface,
  color: colors.textDark,
  fontSize: fontSize.body,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  cursor: 'pointer',
  transition: 'background 0.12s ease, border-color 0.12s ease',
  _hover: { bg: colors.hover },
} as const;

/** The black primary button used by the page header. */
export const primaryButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  h: controlHeight,
  px: 3,
  ...PLAN_TEXT.toolbarTab,
  bg: colors.action,
  color: colors.white,
  borderRadius: PLAN_REVAMP_RADIUS_SM,
  flexShrink: 0,
  cursor: 'pointer',
  _hover: { bg: colors.actionHover },
} as const;

/** Icon-only ghost button. */
export const iconButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  h: '24px',
  w: '24px',
  minW: '24px',
  borderRadius: '6px',
  color: colors.textSecondary,
  cursor: 'pointer',
  _hover: { bg: colors.hover, color: colors.textDark },
} as const;

/** Small neutral pill for counts next to a section title. */
export const CountPill = ({ children }: { children: ReactNode }) => (
  <Text
    {...PLAN_TEXT.badge}
    minW='20px'
    px='8px'
    py='1px'
    textAlign='center'
    whiteSpace='nowrap'
    borderRadius='full'
    bg={colors.headerBg}
    border='1px solid'
    borderColor={colors.border}
    color={colors.textSecondary}
  >
    {children}
  </Text>
);

export type StateTone = 'neutral' | 'danger';

/**
 * Empty / error state: a tinted icon badge, a title and one supporting line, with an optional
 * action. Replaces the bare text placeholders so every "nothing here" screen reads the same.
 */
export const StateBlock = ({
  icon,
  title,
  description,
  action,
  tone = 'neutral',
  compact = false,
}: {
  icon: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  tone?: StateTone;
  compact?: boolean;
}) => (
  <Flex
    direction='column'
    alignItems='center'
    justifyContent='center'
    textAlign='center'
    gap={1}
    h='100%'
    py={compact ? 6 : 12}
    px={4}
  >
    <Flex
      w={compact ? '40px' : '52px'}
      h={compact ? '40px' : '52px'}
      alignItems='center'
      justifyContent='center'
      borderRadius='full'
      bg={tone === 'danger' ? `${colors.danger}14` : colors.activeBg}
      color={tone === 'danger' ? colors.danger : colors.primary}
      mb={2}
    >
      {icon}
    </Flex>
    <Text {...PLAN_TEXT.bodySemibold} color={colors.textDark}>
      {title}
    </Text>
    {description && (
      <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary} maxW='320px'>
        {description}
      </Text>
    )}
    {action && <Box mt={3}>{action}</Box>}
  </Flex>
);
